import { ref, computed, watch } from 'vue'

// Global reactive state for budget data
const transactions = ref([])
const monthlyExpenses = ref([])
const grossMonthlyIncome = ref(0)
const savingsGoal = ref(0)
const budgetStartDate = ref(null) // ISO 8601 date string or null

// Computed values that depend on the reactive state
const currentDate = ref(new Date())

export function useBudgetData() {
  // Update current date (useful for testing different dates)
  const setCurrentDate = (date) => {
    currentDate.value = date
  }

  // Navigate to previous month
  const previousMonth = () => {
    const newDate = new Date(currentDate.value)
    newDate.setMonth(newDate.getMonth() - 1)
    currentDate.value = newDate
  }

  // Navigate to next month
  const nextMonth = () => {
    const newDate = new Date(currentDate.value)
    newDate.setMonth(newDate.getMonth() + 1)
    currentDate.value = newDate
  }

  // Navigate to current month (today)
  const goToCurrentMonth = () => {
    currentDate.value = new Date()
  }

  // Get formatted month display
  const currentMonthDisplay = computed(() => {
    return currentDate.value.toLocaleDateString('en-US', {
      month: 'long',
      year: 'numeric'
    })
  })

  // Transaction management
  const addTransaction = (transaction) => {
    transactions.value.push(transaction)
  }

  const removeTransaction = (index) => {
    transactions.value.splice(index, 1)
  }

  const removeTransactionById = (transactionId) => {
    const index = transactions.value.findIndex(t => t.id === transactionId)
    if (index !== -1) {
      console.log(`Removed transaction by ID: ${transactionId}`);
      transactions.value.splice(index, 1)
      return true
    }
    return false
  }

  const removeTransactionByProperties = (transaction) => {
    const index = transactions.value.findIndex(t =>
      t.date.getTime() === transaction.date.getTime() &&
      t.description === transaction.description &&
      t.amount === transaction.amount &&
      t.type === transaction.type
    )
    if (index !== -1) {
      transactions.value.splice(index, 1)
      return true
    }
    return false
  }

  const updateTransaction = (transactionId, updates) => {
    const transaction = transactions.value.find(t => t.id === transactionId)
    if (transaction) {
      if (updates.description !== undefined) {
        transaction.description = updates.description
      }
      if (updates.amount !== undefined) {
        transaction.amount = updates.amount
      }
      if (updates.type !== undefined) {
        transaction.type = updates.type
      }
      if (updates.category !== undefined) {
        transaction.category = updates.category
      }
      console.log(`Updated transaction: ${transactionId}`)
      return true
    }
    return false
  }

  const updateTransactions = (newTransactions) => {
    transactions.value = newTransactions
  }

  const setTransactions = (apiTransactions) => {
    // Transform API response to match existing transaction format
    const transformedTransactions = apiTransactions.map(apiTransaction => ({
      id: apiTransaction.SK, // Preserve transaction ID for deletion
      description: apiTransaction.description,
      amount: apiTransaction.amount,
      type: apiTransaction.type,
      date: new Date(apiTransaction.createdAt),
      category: apiTransaction.category,
      goal: apiTransaction.goal // Optional goal ID for savings transactions
    }))

    transactions.value = transformedTransactions
    return transformedTransactions.length
  }

  // Monthly expenses management
  const addMonthlyExpense = (expense) => {
    monthlyExpenses.value.push(expense)
  }

  const removeMonthlyExpense = (index) => {
    monthlyExpenses.value.splice(index, 1)
  }

  const updateMonthlyExpenses = (expenses) => {
    monthlyExpenses.value = expenses
  }

  // Budget setup
  const updateGrossIncome = (income) => {
    grossMonthlyIncome.value = income
  }

  const updateSavingsGoal = (goal) => {
    savingsGoal.value = goal
  }

  // Reset all budget data (useful when switching sessions/accounts)
  const resetBudgetData = () => {
    transactions.value = []
    monthlyExpenses.value = []
    grossMonthlyIncome.value = 0
    savingsGoal.value = 0
    budgetStartDate.value = null
    currentDate.value = new Date()
  }

  // Budget start date management with ISO 8601 validation
  const setBudgetStartDate = (dateString) => {
    if (!dateString) {
      budgetStartDate.value = null
      return
    }

    // Validate ISO 8601 format (full timestamp: YYYY-MM-DDTHH:mm:ss.sssZ)
    const isoDateRegex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/
    if (!isoDateRegex.test(dateString)) {
      console.error('Invalid budgetStartDate format:', dateString)
      budgetStartDate.value = null
      return
    }

    // Validate it's a real date
    const date = new Date(dateString)
    if (isNaN(date.getTime())) {
      console.error('Invalid budgetStartDate value:', dateString)
      budgetStartDate.value = null
      return
    }

    budgetStartDate.value = dateString
  }

  // Helper to check if viewing current month
  const isViewingCurrentMonth = computed(() => {
    const now = new Date()
    const viewing = currentDate.value
    return viewing.getMonth() === now.getMonth() &&
      viewing.getFullYear() === now.getFullYear()
  })

  // Get the actual current date (always today, regardless of viewed month)
  const actualCurrentDate = computed(() => new Date())

  // Determines if the currently viewed month is the partial month
  const isPartialMonth = computed(() => {
    if (!budgetStartDate.value) return false
    if (!currentDate.value) return false

    const startDate = new Date(budgetStartDate.value)
    const viewing = currentDate.value

    // If start date is day 1, treat as full month
    // Use local timezone methods to match currentDate context
    if (startDate.getDate() === 1) return false

    // Check if viewing the same month/year as start date
    // Use local timezone methods to match currentDate context
    return viewing.getMonth() === startDate.getMonth() &&
      viewing.getFullYear() === startDate.getFullYear()
  })

  // Calculates the proration factor for partial months
  const prorationFactor = computed(() => {
    if (!isPartialMonth.value) return 1.0

    const startDate = new Date(budgetStartDate.value)
    // Use local timezone methods to match currentDate context
    const startDay = startDate.getDate()
    const totalDays = daysInMonth.value
    const remainingDays = totalDays - startDay + 1

    return remainingDays / totalDays
  })

  // Computed properties for current month transactions
  const currentMonthTransactions = computed(() => {
    const now = currentDate.value
    return transactions.value.filter(t => {
      const transactionDate = new Date(t.date)
      return transactionDate.getMonth() === now.getMonth() &&
        transactionDate.getFullYear() === now.getFullYear()
    })
  })

  // Basic calculations
  const totalMonthlyExpenses = computed(() => {
    return monthlyExpenses.value.reduce((sum, expense) => sum + expense.amount, 0)
  })

  const monthlyIncome = computed(() => {
    const grossIncome = grossMonthlyIncome.value || 0
    const expenses = totalMonthlyExpenses.value
    const netIncome = Math.max(0, grossIncome - expenses)

    // Apply proration if in partial month
    if (isPartialMonth.value) {
      const prorated = netIncome * prorationFactor.value
      return Math.round(prorated * 100) / 100 // Round to 2 decimal places
    }

    return netIncome
  })

  const daysInMonth = computed(() => {
    const now = currentDate.value
    return new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate()
  })

  /**
   * Daily Budget Calculation (Fixed Daily Allowance)
   * 
   * This calculates the theoretical daily spending allowance if the monthly income
   * were divided equally across the relevant days. This is a FIXED amount that
   * doesn't change based on actual spending.
   * 
   * Formula (Full Month): (Monthly Income After Fixed Expenses) ÷ (Total Days in Month)
   * Formula (Partial Month): (Prorated Monthly Income) ÷ (Remaining Days in Month)
   * 
   * Full month example:
   * - Gross monthly income: $3,000
   * - Fixed monthly expenses (rent, utilities, etc.): $1,500
   * - Net monthly income: $3,000 - $1,500 = $1,500
   * - Days in current month: 30
   * - Daily budget: $1,500 ÷ 30 = $50.00 per day
   * 
   * Partial month example:
   * - Net monthly income: $1,500
   * - Proration factor: 0.5333 (16 days remaining out of 30)
   * - Prorated monthly income: $1,500 × 0.5333 = $800
   * - Remaining days: 16
   * - Daily budget: $800 ÷ 16 = $50.00 per day
   * 
   * Key Differences from dailyRemainingBudget:
   * - dailyBudget: Fixed theoretical allowance (doesn't change with spending)
   * - dailyRemainingBudget: Dynamic allowance based on actual remaining budget
   * 
   * Use Cases:
   * - Setting initial daily spending targets
   * - Comparing actual vs. planned daily spending
   * - Budget planning and goal setting
   */
  const dailyBudget = computed(() => {
    if (isPartialMonth.value) {
      // For partial months, divide by remaining days
      const startDate = new Date(budgetStartDate.value)
      // Use local timezone methods to match currentDate context
      const startDay = startDate.getDate()
      const totalDays = daysInMonth.value
      const remainingDays = totalDays - startDay + 1
      const daily = monthlyIncome.value / remainingDays
      return Math.round(daily * 100) / 100 // Round to 2 decimal places
    }

    // For full months, divide by total days
    const daily = monthlyIncome.value / daysInMonth.value
    return Math.round(daily * 100) / 100 // Round to 2 decimal places
  })

  // Transaction totals
  const totalDebits = computed(() => {
    return currentMonthTransactions.value
      .filter(t => t.type === 'debit')
      .reduce((sum, t) => sum + t.amount, 0)
  })

  const totalCredits = computed(() => {
    return currentMonthTransactions.value
      .filter(t => t.type === 'credit')
      .reduce((sum, t) => sum + t.amount, 0)
  })

  const remainingBudget = computed(() => {
    return monthlyIncome.value + totalCredits.value - totalDebits.value
  })

  /**
   * Daily Remaining Budget Calculation
   * 
   * This calculates how much money can be spent per day for the rest of the current month
   * to stay within the overall monthly budget.
   * 
   * Formula: (Monthly Budget Remaining) ÷ (Days Left in Month Including Today)
   * 
   * Example:
   * - Monthly income after expenses: $1,500
   * - Already spent this month: $800 (debits) - $200 (credits) = $600 net spent
   * - Remaining budget: $1,500 - $600 = $900
   * - Today is the 15th of a 30-day month
   * - Days remaining including today: 30 - 15 + 1 = 16 days
   * - Daily remaining budget: $900 ÷ 16 = $56.25 per day
   * 
   * This amount adjusts daily as:
   * 1. Transactions are added (reducing remaining budget)
   * 2. Days pass (reducing remaining days)
   * 
   * If the result is negative, it means the monthly budget has been exceeded.
   */
  const dailyRemainingBudget = computed(() => {
    const today = currentDate.value
    const remainingDays = daysInMonth.value - today.getDate() + 1
    return remainingBudget.value / remainingDays
  })

  return {
    // State
    transactions,
    monthlyExpenses,
    grossMonthlyIncome,
    savingsGoal,
    budgetStartDate,
    currentDate,

    // Actions
    addTransaction,
    removeTransaction,
    removeTransactionById,
    removeTransactionByProperties,
    updateTransaction,
    updateTransactions,
    setTransactions,
    addMonthlyExpense,
    removeMonthlyExpense,
    updateMonthlyExpenses,
    updateGrossIncome,
    updateSavingsGoal,
    resetBudgetData,
    setBudgetStartDate,
    setCurrentDate,
    previousMonth,
    nextMonth,
    goToCurrentMonth,

    // Computed values
    currentMonthTransactions,
    totalMonthlyExpenses,
    monthlyIncome,
    daysInMonth,
    dailyBudget,
    totalDebits,
    totalCredits,
    remainingBudget,
    dailyRemainingBudget,
    isViewingCurrentMonth,
    actualCurrentDate,
    currentMonthDisplay,
    isPartialMonth,
    prorationFactor
  }
}